import { services } from "onearm";
import { BaseGameState } from "onearm";
import { PaysAct } from "onearm";
import { FreeSpinsTriggerAct } from "onearm";
import { StopReelsAct } from "onearm";
import { StickySymbolsAct } from "onearm";
import { CascadeAct } from "onearm";
import { MultiplierAct } from "../../acts/MultiplierAct";
import { GameStates } from "onearm";
import { FreeSpinRetriggerAct } from "../../acts/FreeSpinRettrigerAct";
import { HUDController } from "../../hud/HUDController";
import gsap from "gsap";
import { GSDevTools } from "gsap/GSDevTools";

export default class GameState extends BaseGameState {
    enter() {
        const gameConfig = services.gameConfig;

        this.autoplay = services.get("autoplay");
        this.fullscreenService = services.get("fullscreen");

        this.autoplay.onComplete.connect(this.handleStopAutoplay.bind(this));

        this.gameBackgroundScene = this.scenes.add("GameBackground", {
            spine: "BackgroundAnimation",
            portraitAnimation: "bg_mobile",
            landscapeAnimation: "bg_desctop",
        });
        this.scenes.show("GameBackground");

        const item = this.gameLogic.getFreeSpinsItems()[0];

        const hudScene = this.scenes.add("HUDScene", { layer: "hud" });
        this.scenes.show("HUDScene");

        this.hud = new HUDController(hudScene, {
            balance: this.gameLogic.balance,
            bet: this.gameLogic.bet,
            currencyFormatter: services.get("currencyFormatter"),
            volume: this.data.volume,
            musicVolume: this.data.musicVolume,
            sfxVolume: this.data.sfxVolume,
            isSoundsMuted: this.data.isSoundsMuted,
            isMusicMuted: this.data.isMusicMuted,
            isSfxMuted: this.data.isSfxMuted,
            spinType: this.data.spinType ?? "normal",
            freeSpinsPrice: item?.price ?? 0,
            freeSpinsCount: item?.number?.spins ?? 0,
            doubleChanceEnabled: this.gameLogic.feature?.toggle ?? false,
        });

        this.hud.onVolumeChange.connect(this.handleVolumeChange.bind(this));
        this.hud.onMutedChange.connect(this.handleMutedChange.bind(this));
        this.hud.onPressFullScreen.connect(this.handleFullScreenToggle.bind(this));
        this.hud.setIdleState();
        
        this.actsByName = {
            pays: result =>
                new PaysAct({
                    result,
                    reelsScene: this.reels,
                    hud: this.hud,
                    gameLogic: this.gameLogic,
                    scenes: this.scenes,
                    data: this.data,
                }),
            freeSpinsTrigger: result =>
                new FreeSpinsTriggerAct({
                    result,
                    reelsScene: this.reels,
                    hud: this.hud,
                    gameLogic: this.gameLogic,
                    scenes: this.scenes,
                }),
            stickySymbols: result => new StickySymbolsAct({ result, reelsScene: this.reels, data: this.data }),
            cascade: result => new CascadeAct({ result, reelsScene: this.reels, data: this.data }),
            multiplier: result => new MultiplierAct({ result, reelsScene: this.reels, hud: this.hud, gameLogic: this.gameLogic, data: this.data }),
            freeSpinsRetrigger: result => new FreeSpinRetriggerAct({ result, hud: this.hud, data: this.data }),
        };

        this.reels = this.scenes.add("ReelsScene", {
            root: hudScene.getReelsPlaceholder(),
            gameConfig,
            lastSpin: this.gameLogic.lastSpin,
        });
        this.scenes.show("ReelsScene");

        this.bets = services.get("bets");
        this.bets.onTotalBetChanged.connect(this.updateTotalBet.bind(this));
        this.data.on("spinType", this.handleChangeSpinType.bind(this));

        this.audio.setAllVolume(this.data.volume);
        this.audio.setVolumeMusic(this.data.musicVolume);
        this.audio.setVolumeSfx(this.data.sfxVolume);
        this.audio.muteAll(this.data.isSoundsMuted);
        this.audio.muteMusic(this.data.isMusicMuted);
        this.audio.muteSfx(this.data.isSfxMuted);
        this.audio.playMusic("basegame_loop", { loop: true });

        if (this.gameLogic.hasError()) {
            this.goTo(GameStates.ERROR, this.gameLogic.hasError());
            return;
        }
        if (this.gameLogic.hasFreeSpins()) {
            this.goTo(GameStates.FREE_SPIN_INTRO);
        } else {
            this.goTo(GameStates.IDLE);
        }


        // gsap.registerPlugin(GSDevTools)
        // GSDevTools.create({
        //     container: document.getElementById("gsap-dev-tools"),
        //     animation: this.debugMultiplierAnimation()
        // });
    }

    setFreeSpinsMode() {
        this.gameBackgroundScene.changeAnimation({
            spine: "BackgroundBonusAnimation",
            portraitAnimation: "bg_mobile",
            landscapeAnimation: "bg_desctop",
        });
    }

    setBaseGameMode() {
        this.gameBackgroundScene.changeAnimation({
            spine: "BackgroundAnimation",
            portraitAnimation: "bg_mobile",
            landscapeAnimation: "bg_desctop",
        });
    }

    handleStopAutoplay() {
        this.hud.showAutoplayButton();
    }

    handleVolumeChange(volume) {
        this.data.volume = volume;
        this.audio.setAllVolume(volume);
    }

    handleMutedChange(muted) {
        this.data.muted = muted;
        this.audio.muteAll(muted);
    }

    handleChangeSpinType(value) {
        this.hud.setSpinType(value);
    }

    handleFullScreenToggle() {
        if (this.fullscreenService.isSupported) {
            this.fullscreenService.toggleFullscreen();
        }
    }

    async updateTotalBet(value) {
        const options = await services.gameLogic.setBet(value);

        this.setBetOptions(options);
    }

    setBetOptions(options) {
        const { bet_sum, feature } = options;
        const totalBet  = feature?.toggle ? feature?.bet_sum : bet_sum;

        this.hud.setTotalBet(totalBet);

        if (feature && feature.bet_sum) {
            this.hud.setDoubleChanceBet(feature?.bet_sum);
        } else {
            this.hud.setDoubleChanceBet(0);
        }

        const item = this.gameLogic.getBuyBonusItems()[0];

        this.hud.setFreeSpins(item.number.spins);
        this.hud.setSelectedPrice(item.price);
    }

    getFirstFreeSpinsPrice() {
        const items = this.gameLogic.getFreeSpinsItems();
        const item = items[0];

        return this.currencyFormatter.format(item ? item.price : 0);
    }

    update(dt) {
        super.update(dt);
    }

    /**
     * @param {Object} result - Spin result
     * @param {Object} [actsByName={}] - Custom acts by type name
     * @param {Object} [options={}] - Additional options
     * @param {boolean} [options.quickStop=false] - Use quick stop animation for reels
     * @returns {Array} - Array of presentation acts
     */
    makeSpinResultPresentation(result, actsByName = {}, options = {}) {
        const actions = [];
        const { results } = result;
        const { quickStop = false } = options;

        for (let i = 0; i < results.length; i++) {
            const result = results[i];
            const { type } = result;

            if (type === "stop") {
                actions.push(new StopReelsAct({ reelsScene: this.reels, result, quickStop, data: this.data }));
            }

            const act = actsByName[type] || this.actsByName[type];

            if (act) {
                actions.push(act(result));
            }
        }

        return actions;
    }

    exit() {
        this.hud?.destroy();
        super.exit();
    }

    /**
     * @description Sandbox function for debugging MultiplierAct animation
     * @param {Object} [options] - Override default test values
     * @param {Array<{row: number, column: number, multiplier: number}>} [options.multipliers] - Multiplier positions
     * @param {number} [options.totalMultiplier] - Total multiplier value
     * @param {number} [options.winBeforeMultiplier] - Win before applying multiplier
     */
    debugMultiplierAnimation(options = {}) {
        const {
            multipliers = [
                { row: 0, column: 0, multiplier: 2 },
                { row: 1, column: 2, multiplier: 3 },
                { row: 2, column: 4, multiplier: 5 },
            ],
            totalMultiplier = 10,
            winBeforeMultiplier = 100,
        } = options;

        const fakeResult = {
            multipliers,
            multiplier: totalMultiplier,
            win: {
                total: winBeforeMultiplier * totalMultiplier,
                beforeMultiplier: winBeforeMultiplier,
            },
        };

        const act = new MultiplierAct({
            result: fakeResult,
            reelsScene: this.reels,
            hud: this.hud,
            gameLogic: this.gameLogic,
        });

        const timeline = act.action();
        timeline.pause();

        return timeline;
    }
}
